import { createElement, useCallback, useEffect, useState, type RefObject } from 'react';
import { createRoot } from 'react-dom/client';
import type Konva from 'konva';
import { SEQUENCE } from '../constants';
import { useDiagramStore } from '../stores/diagramStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useUIStore } from '../stores/uiStore';
import { sanitizeFileStem } from '../utils';
import { estimateSequenceMessageFootprint } from '../utils/sequenceMessageLayout';
import { registerExportFunctions } from '../utils/pngExporter';
import { DiagramExportStage } from '../components/export/DiagramExportStage';
import {
  buildExportSnapshotFromPage,
  computeExportStageLayout,
  type ExportDiagramSnapshot,
} from '../utils/exportBounds';
import type { DiagramPage } from '../types';

const SEQUENCE_MIN_LABEL_WIDTH = SEQUENCE.MIN_LABEL_WIDTH;
const SEQUENCE_LABEL_SAFE_MARGIN = SEQUENCE.LABEL_SAFE_MARGIN;

interface UseDiagramExportArgs {
  stageRef: RefObject<Konva.Stage>;
  sequenceLifelineBottom: number;
  projectFileStem: string;
  activeProjectName: string;
  showToast: (message: string) => void;
}

export function useDiagramExport({
  stageRef,
  sequenceLifelineBottom,
  projectFileStem,
  activeProjectName,
  showToast,
}: UseDiagramExportArgs) {
  const [exportAllPagesAsPng, setExportAllPagesAsPng] = useState(false);
  const [exportPngMonochrome, setExportPngMonochrome] = useState(false);
  const [isPngExporting, setIsPngExporting] = useState(false);

  const {
    diagramType, participants, messages, fragments,
    nodes, viewport, pages, activePage,
  } = useDiagramStore();
  const { palette, iconStyle, socketLabelVisibility, defaultRoutingMode, forceLayoutEdges, tintEdgesByDestination, sequenceMessageNumbering } = useSettingsStore();
  const { setPngExportModalOpen } = useUIStore();

  const activePageEntry = pages.find(page => page.id === activePage) ?? null;

  const handleExportImage = useCallback(async (format: 'png' | 'svg' = 'png') => {
    if (!stageRef.current) return;
    try {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

      if (diagramType === 'flow') {
        nodes.forEach(node => {
          minX = Math.min(minX, node.position.x);
          maxX = Math.max(maxX, node.position.x + node.size.width);
          minY = Math.min(minY, node.position.y);
          maxY = Math.max(maxY, node.position.y + node.size.height);
        });
      }

      if (diagramType === 'sequence') {
        const HEADER_WIDTH = 140;
        const HEADER_HEIGHT = 44;
        const HEADER_Y = 40;
        const lifelineBottom = sequenceLifelineBottom;

        if (participants.length > 0) {
          const minParticipantX = Math.min(...participants.map(p => p.x));
          const maxParticipantX = Math.max(...participants.map(p => p.x + HEADER_WIDTH));
          minX = minParticipantX;
          maxX = maxParticipantX;
          minY = HEADER_Y;
          maxY = lifelineBottom + HEADER_HEIGHT;
        }

        if (messages.length > 0) {
          messages.forEach((message) => {
            const footprint = estimateSequenceMessageFootprint(
              participants,
              message.label,
              message.sourceId,
              message.targetId,
              { safeMargin: SEQUENCE_LABEL_SAFE_MARGIN, minLabelWidth: SEQUENCE_MIN_LABEL_WIDTH },
            );
            minY = Math.min(minY, message.y - footprint.above);
            maxY = Math.max(maxY, message.y + footprint.below);
          });
        }

        if (fragments.length > 0) {
          fragments.forEach(frag => {
            minY = Math.min(minY, frag.startY);
            maxY = Math.max(maxY, frag.endY);
          });
        }
      }

      if (!isFinite(minX)) {
        minX = 0;
        maxX = 800;
        minY = 0;
        maxY = 600;
      }

      const stage = stageRef.current;
      const stageWidth = stage.width() as number;
      const stageHeight = stage.height() as number;
      const scale = Math.max(0.05, viewport.scale);
      const screenMinX = minX * scale + viewport.x;
      const screenMaxX = maxX * scale + viewport.x;
      const screenMinY = minY * scale + viewport.y;
      const screenMaxY = maxY * scale + viewport.y;
      const screenWidth = Math.max(1, screenMaxX - screenMinX);
      const screenHeight = Math.max(1, screenMaxY - screenMinY);
      const padding = Math.max(24, Math.round(Math.max(screenWidth, screenHeight) * 0.08));

      const exportX = Math.max(0, Math.floor(screenMinX - padding));
      const exportY = Math.max(0, Math.floor(screenMinY - padding));
      const exportWidth = Math.max(1, Math.ceil(Math.min(stageWidth - exportX, screenWidth + padding * 2)));
      const exportHeight = Math.max(1, Math.ceil(Math.min(stageHeight - exportY, screenHeight + padding * 2)));
      const pixelRatio = Math.max(1.25, Math.min(5, 2 / scale));

      const rawDataUrl = stage.toDataURL({
        x: exportX,
        y: exportY,
        width: exportWidth,
        height: exportHeight,
        pixelRatio,
      });

      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new window.Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to build export image'));
        image.src = rawDataUrl;
      });

      if (format === 'svg') {
        const svgMarkup = [
          `<svg xmlns="http://www.w3.org/2000/svg" width="${img.width}" height="${img.height}" viewBox="0 0 ${img.width} ${img.height}">`,
          `<rect width="100%" height="100%" fill="${palette.canvasBg}" />`,
          `<image href="${rawDataUrl}" width="${img.width}" height="${img.height}" />`,
          '</svg>',
        ].join('');
        const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${projectFileStem}-${new Date().toISOString().split('T')[0]}.svg`;
        link.click();
        URL.revokeObjectURL(link.href);
        return;
      }

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = img.width;
      exportCanvas.height = img.height;
      const ctx = exportCanvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context unavailable');
      ctx.fillStyle = palette.canvasBg;
      ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement('a');
      link.href = exportCanvas.toDataURL('image/png');
      link.download = `${projectFileStem}-${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    } catch (err) {
      console.error('Failed to export image:', err);
      alert('Failed to export diagram as image');
    }
  }, [stageRef, diagramType, nodes, participants, messages, fragments, sequenceLifelineBottom, viewport, palette.canvasBg, projectFileStem]);

  const buildCurrentExportSnapshot = useCallback((scope: 'currentView' | 'rootPage' = 'currentView'): ExportDiagramSnapshot => {
    const state = useDiagramStore.getState();
    const rootGraph = state.graphs?.root;
    const useRootGraph = scope === 'rootPage' && !!rootGraph;
    const exportPage: DiagramPage = {
      id: activePageEntry?.id ?? 'current-page',
      name: activePageEntry?.name?.trim() || activeProjectName,
      diagramType: state.diagramType,
      nodes: state.nodes,
      edges: state.edges,
      groups: state.groups,
      layers: state.layers,
      viewport: state.viewport,
      participants: state.participants,
      messages: state.messages,
      fragments: state.fragments,
      participantGroups: state.participantGroups,
      seqLifelineLength: state.seqLifelineLength,
      settings: activePageEntry?.settings,
      graphs: useRootGraph && rootGraph
        ? { root: rootGraph }
        : undefined,
    };

    return buildExportSnapshotFromPage(exportPage);
  }, [activePageEntry?.id, activePageEntry?.name, activePageEntry?.settings, activeProjectName]);

  const buildPngFileName = useCallback((pageName: string, includePageName: boolean) => {
    const dateStamp = new Date().toISOString().split('T')[0];
    if (!includePageName) {
      return `${projectFileStem}-${dateStamp}.png`;
    }
    return `${projectFileStem}-${sanitizeFileStem(pageName)}-${dateStamp}.png`;
  }, [projectFileStem]);

  const downloadDataUrl = useCallback((fileName: string, dataUrl: string) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    link.click();
  }, []);

  const renderSnapshotToPngDataUrl = useCallback(async (snapshot: ExportDiagramSnapshot) => {
    const layout = computeExportStageLayout(snapshot);
    const exportSocketLabelVisibility = socketLabelVisibility ?? 'always';
    // The page's own choices travel with the snapshot, so exporting a page you
    // are not looking at still renders it the way that page is drawn.
    const exportRoutingMode = snapshot.settings?.routingMode ?? defaultRoutingMode ?? 'bezier';
    const exportTextScale = snapshot.settings?.textScale ?? 1;
    const exportForceLayoutEdges = forceLayoutEdges ?? false;
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-100000px';
    container.style.top = '0';
    container.style.width = `${layout.width}px`;
    container.style.height = `${layout.height}px`;
    container.style.pointerEvents = 'none';
    container.style.opacity = '0';
    document.body.appendChild(container);

    const root = createRoot(container);
    let stageNode: Konva.Stage | null = null;

    try {
      await new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          reject(new Error('Timed out while rendering export stage'));
        }, 8000);

        root.render(
          createElement(DiagramExportStage, {
            snapshot,
            stageWidth: layout.width,
            stageHeight: layout.height,
            viewport: layout.viewport,
            palette,
            iconStyle,
            socketLabelVisibility: exportSocketLabelVisibility,
            defaultRoutingMode: exportRoutingMode,
            textScale: exportTextScale,
            forceLayoutEdges: exportForceLayoutEdges,
            tintEdgesByDestination: tintEdgesByDestination ?? true,
            sequenceMessageNumbering: sequenceMessageNumbering ?? true,
            monochrome: exportPngMonochrome,
            onStageReady: (stage: Konva.Stage) => {
              stageNode = stage;
              window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                  window.clearTimeout(timeoutId);
                  resolve();
                });
              });
            },
          }),
        );
      });

      if (!stageNode) {
        throw new Error('Export stage was not created');
      }

      const stage = stageNode as Konva.Stage;
      stage.draw();
      return stage.toDataURL({ pixelRatio: 1 });
    } finally {
      root.unmount();
      container.remove();
    }
  }, [
    defaultRoutingMode,
    forceLayoutEdges,
    tintEdgesByDestination,
    iconStyle,
    palette,
    socketLabelVisibility,
    sequenceMessageNumbering,
    exportPngMonochrome,
  ]);

  const handleConfirmPngExport = useCallback(async () => {
    setIsPngExporting(true);
    try {
      if (exportAllPagesAsPng && pages.length > 1) {
        const activeRootSnapshot = buildCurrentExportSnapshot('rootPage');
        const pageSnapshots = pages.map((page) =>
          page.id === activePage ? activeRootSnapshot : buildExportSnapshotFromPage(page),
        );

        for (let index = 0; index < pageSnapshots.length; index += 1) {
          const snapshot = pageSnapshots[index];
          const dataUrl = await renderSnapshotToPngDataUrl(snapshot);
          downloadDataUrl(buildPngFileName(snapshot.name, true), dataUrl);
          if (index < pageSnapshots.length - 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 120));
          }
        }

        showToast(`Exported ${pageSnapshots.length} PNG files`);
      } else {
        const snapshot = buildCurrentExportSnapshot('currentView');
        const dataUrl = await renderSnapshotToPngDataUrl(snapshot);
        downloadDataUrl(buildPngFileName(snapshot.name, pages.length > 1), dataUrl);
        showToast('Exported PNG');
      }

      setPngExportModalOpen(false);
    } catch (error) {
      console.error('Failed to export PNG:', error);
      alert('Failed to export diagram as PNG');
    } finally {
      setIsPngExporting(false);
    }
  }, [
    activePage,
    buildCurrentExportSnapshot,
    buildPngFileName,
    downloadDataUrl,
    exportAllPagesAsPng,
    pages,
    renderSnapshotToPngDataUrl,
    setPngExportModalOpen,
    showToast,
  ]);

  useEffect(() => {
    registerExportFunctions({
      renderSnapshotToPng: async (snapshot) => {
        const dataUrl = await renderSnapshotToPngDataUrl(snapshot);
        return dataUrl.split(',')[1] || dataUrl;
      },
      buildFileName: buildPngFileName,
      getProjectName: () => projectFileStem,
      getPages: () => pages,
      buildExportSnapshotFromPage,
      buildCurrentExportSnapshot,
    });
  }, [renderSnapshotToPngDataUrl, buildPngFileName, projectFileStem, pages, buildCurrentExportSnapshot]);

  return {
    exportAllPagesAsPng,
    setExportAllPagesAsPng,
    exportPngMonochrome,
    setExportPngMonochrome,
    isPngExporting,
    handleExportImage,
    handleConfirmPngExport,
  };
}
